import { getBaseUrl } from './ServiceUtils';
import appRequest from './appRequest';
import { getToken } from './TokenStorage';
import { orderTypes, STOP_TYPES } from '../utils/OrderUtils';
import dateUtils from '../utils/DateUtils';

const ORDERS_URL = getBaseUrl('/orders/');

export async function getOrders(symbol, page, environment, size, strategyId, market) {
    const params = new URLSearchParams({ page: page || 1 });
    if (environment) params.append('environment', environment);
    if (size) params.append('size', size);
    if (strategyId) params.append('strategyId', strategyId);
    if (market) params.append('market', market); // 'futures' | 'spot' — lente de mercado
    return await appRequest.doGetRequest(`${ORDERS_URL}${symbol}?${params.toString()}`);
}

// Visão pareada com pareamento cross-page (servidor pareia FIFO sobre todas as ordens do
// filtro e pagina por pares). Retorna { rows: [{type, buy, sell}], count }.
export async function getOrderPairs(symbol, page, environment, size, strategyId, market) {
    const params = new URLSearchParams({ page: page || 1 });
    if (environment) params.append('environment', environment);
    if (size) params.append('size', size);
    if (strategyId) params.append('strategyId', strategyId);
    if (market) params.append('market', market); // 'futures' | 'spot' — filtra por mercado
    return await appRequest.doGetRequest(`${ORDERS_URL}pairs/${symbol || ''}?${params.toString()}`);
}

// Candles + marcadores das ordens (compra/venda) pro gráfico in-app (lightweight-charts).
// Retorna { symbol, interval, candles: [{time,open,high,low,close}], markers: [{time,side,price,quantity,strategyName}] }.
export async function getOrderChart(symbol, interval, environment, strategyId, limit) {
    const params = new URLSearchParams();
    if (interval) params.append('interval', interval);
    if (environment) params.append('environment', environment);
    if (strategyId) params.append('strategyId', strategyId);
    if (limit) params.append('limit', limit);
    return await appRequest.doGetRequest(`${ORDERS_URL}chart/${symbol}?${params.toString()}`);
}

export async function cancelOrder(symbol, orderId) {
    return await appRequest.doDeleteRequest(`${ORDERS_URL}${symbol}/${orderId}`);
}

export async function syncOrder(beholderOrderId) {
    return await appRequest.doPostRequest(`${ORDERS_URL}${beholderOrderId}/sync`, null);
}

export async function placeOrder(order) {
    const postOrder = {
        symbol: order.symbol,
        quantity: order.quantity,
        side: order.side.toUpperCase(),
        options: {
            type: order.type.toUpperCase()
        }
    };

    if (postOrder.options.type === orderTypes.MARKET && order.isQuote) {
        postOrder.options.quoteOrderQty = order.quantity;
        delete postOrder.quantity;
    }

    // Ordens aonde o preço precisa ter valor definido
    if ([orderTypes.LIMIT, orderTypes.STOP_LOSS_LIMIT, orderTypes.TAKE_PROFIT_LIMIT, orderTypes.TRAILING_STOP].includes(postOrder.options.type))
        postOrder.limitPrice = order.limitPrice;

    if (STOP_TYPES.includes(postOrder.options.type))
        postOrder.options.stopPrice = order.stopPrice;

    if (postOrder.options.type === orderTypes.TRAILING_STOP)
        postOrder.options.stopPriceMultiplier = order.stopPriceMultiplier;

    return await appRequest.doPostRequest(ORDERS_URL, postOrder);
}

// P&L REALIZADO por dia (spot, FIFO) — USDT e %. tzOffset do navegador pra fechar o dia local.
export async function getDailyRealized(quote, days = 30, environment) {
    const params = { days, tzOffset: -new Date().getTimezoneOffset() };
    if (environment) params.environment = environment;
    const requestParams = new URLSearchParams(params).toString();
    return await appRequest.doGetRequest(`${ORDERS_URL}reports-daily/${quote}?${requestParams}`);
}

export async function getOrdersReport(symbol, automationId, startDate, endDate, environment, market) {
    startDate = startDate ? startDate.getTime() : dateUtils.getThirtyDaysAgo();
    endDate = endDate ? endDate.getTime() : dateUtils.getTodayEnd();

    const params = { startDate, endDate };
    if (automationId) params.automationId = automationId;
    if (environment) params.environment = environment;
    if (market) params.market = market; // 'futures' | 'spot' — lente de mercado

    const requestParams = new URLSearchParams(params).toString();

    return await appRequest.doGetRequest(`${ORDERS_URL}reports/${symbol}?${requestParams}`);
}

export async function getPerformance(startDate, endDate, environment, market) {
    startDate = startDate ? startDate.getTime() : dateUtils.getThirtyDaysAgo();
    endDate = endDate ? endDate.getTime() : dateUtils.getTodayEnd();

    const params = { startDate, endDate };
    if (environment) params.environment = environment;
    if (market) params.market = market; // 'futures' | 'spot' — lente de mercado

    return await appRequest.doGetRequest(`${ORDERS_URL}performance?${new URLSearchParams(params).toString()}`);
}

export async function getBusinessHealth() {
    return await appRequest.doGetRequest(`${ORDERS_URL}businessHealth`);
}

export async function getPostMortem(orderId) {
    return await appRequest.doGetRequest(`${ORDERS_URL}${orderId}/postMortem`);
}

/**
 * Baixa o relatório de performance em Markdown como arquivo.
 * Usa fetch direto pra receber blob em vez de JSON parse.
 */
export async function downloadPerformanceReport(startDate, endDate, environment) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.getTime ? startDate.getTime() : startDate);
    if (endDate) params.append('endDate', endDate.getTime ? endDate.getTime() : endDate);
    if (environment) params.append('environment', environment);

    const token = getToken();
    const response = await fetch(`${ORDERS_URL}performanceReport?${params.toString()}`, {
        method: 'GET',
        headers: token ? { authorization: token } : {}
    });

    if (!response.ok) {
        throw new Error(`Falha ao gerar relatório (HTTP ${response.status})`);
    }

    const blob = await response.blob();
    const cd = response.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `hydra-report-${Date.now()}.md`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export async function getDayTradeReport(symbol, automationId, date, environment, market) {
    date = date ? date.getTime() : dateUtils.getTodayStart();

    const params = { date };
    if (automationId) params.automationId = automationId;
    if (environment) params.environment = environment;
    if (market) params.market = market; // 'futures' | 'spot' — lente de mercado

    const requestParams = new URLSearchParams(params).toString();

    return await appRequest.doGetRequest(`${ORDERS_URL}reports/${symbol}?${requestParams}`);
}

export async function getAiSignalHistory(symbol, limit = 50) {
    const params = new URLSearchParams({ limit });
    if (symbol) params.set('symbol', symbol);
    return await appRequest.doGetRequest(`${ORDERS_URL}aiSignals?${params}`);
}

export async function getRegimeHistory(startDate, endDate, symbol) {
    const params = new URLSearchParams({ startDate, endDate });
    if (symbol) params.set('symbol', symbol);
    return await appRequest.doGetRequest(`${ORDERS_URL}regimeHistory?${params}`);
}

export async function getFocusHistory(startDate, endDate, symbol) {
    const params = new URLSearchParams({ startDate, endDate });
    if (symbol) params.set('symbol', symbol);
    return await appRequest.doGetRequest(`${ORDERS_URL}focusHistory?${params}`);
}